// src/utils/loadLetterGenerator.ts

import {
  LoadLetter,
  LoadLetterClass,
  LoadLetterAllowance,
  TemplateVariables,
  Semester,
  SemesterLoad,
} from '../types/loadLetter';
import { renderTemplate } from './templateRenderer';
import { getDefaultTemplate, getTemplateById, getLoadLetterSettings } from '../services/loadLetterService';
import { resolveClassMinutes } from './classMinutes';
import { getEffectiveAllowanceMinutes, allowanceAppliesToYear } from '../types/staff';

/**
 * Load Letter Generator
 *
 * Generates load letters from staff data, grid assignments, and allowances
 */

export interface StaffData {
  id: string;
  name: string;
  staffCode: string;
  initial: string;
  department: string;
  curriculumArea?: string;
  fte: number;
  baseMinutesPerFortnight: number;
  semester1: {
    allowances: any[];
    allocatedMinutes: number;
  };
  semester2: {
    allowances: any[];
    allocatedMinutes: number;
  };
  both?: {
    allowances: any[];
  };
}

export interface GridData {
  classes: any[];
  // Grid-level default minutes per class/line. Used as the minutes fallback
  // for classes that carry neither their own classMinutes nor a
  // sourceGridDefaultMinutes (see extractRuahClasses / extractSemesterClasses).
  defaultClassMinutes?: number;
}

export interface LineData {
  id: string;
  name?: string;          // Line name for RUAH detection (e.g., "Line A", "Line B")
  studyPeriod?: string;   // Study period for RUAH detection (e.g., "term1", "semester1")
  semester: 'semester1' | 'semester2';
  minutesPerFortnight: number;
}

interface GenerateOptions {
  academicYear: string;
  semester: Semester;
  issueDateOffset?: number;  // Days from now for issue date
  dueDateOffset?: number;    // Days from now for due date
  templateId?: string;       // Optional specific template, otherwise use default
  userId: string;            // User generating the letter
}

/**
 * Generate a single load letter for a staff member
 */
export const generateLoadLetter = async (
  staff: StaffData,
  grid: GridData,
  lines: LineData[],
  options: GenerateOptions
): Promise<Omit<LoadLetter, 'id' | 'createdAt' | 'updatedAt'>> => {
  // Validate staff data structure
  if (!staff.semester1) {
    throw new Error(`Staff missing semester1 data`);
  }
  if (!staff.semester2) {
    throw new Error(`Staff missing semester2 data`);
  }
  if (staff.fte == null || !staff.baseMinutesPerFortnight) {
    throw new Error(`Staff missing FTE or baseMinutesPerFortnight`);
  }
  if (staff.fte <= 0) {
    // Callers skip 0-FTE staff (no teaching load to report); refuse rather
    // than let a pro-rata or default turn it into a full-time letter (#288).
    throw new Error(`Staff has 0 FTE; no load letter generated`);
  }

  // Validate grid and lines
  if (!grid) {
    throw new Error(`Grid is required`);
  }
  if (!lines || !Array.isArray(lines)) {
    throw new Error(`Lines must be an array`);
  }

  // Get template
  const template = options.templateId
    ? await getTemplateById(options.templateId)
    : await getDefaultTemplate();

  if (!template) {
    throw new Error('No template available for load letter generation');
  }

  // Calculate dates
  const now = new Date();
  const issueDate = new Date(now);
  issueDate.setDate(issueDate.getDate() + (options.issueDateOffset || 0));

  const dueDate = new Date(now);
  dueDate.setDate(dueDate.getDate() + (options.dueDateOffset || 10));

  // Extract classes for each semester
  const semester1Classes = extractClassesForSemester(staff, grid, lines, 'semester1');
  const semester2Classes = extractClassesForSemester(staff, grid, lines, 'semester2');

  // Extract RUAH term classes
  const ruahTerm1Classes = extractRuahClasses(staff, grid, lines, 'term1');
  const ruahTerm2Classes = extractRuahClasses(staff, grid, lines, 'term2');
  const ruahTerm3Classes = extractRuahClasses(staff, grid, lines, 'term3');
  const ruahTerm4Classes = extractRuahClasses(staff, grid, lines, 'term4');

  // Extract allowances
  const allowances = extractAllowances(staff, options.semester, parseInt(options.academicYear, 10));

  // Target is minutes per fortnight, so every figure it is compared with must
  // be one semester's fortnight too. RUAH: Term 1 counts in Semester 1, Term 3
  // in Semester 2 (matching LoadSummaries.tsx).
  const targetMinutes = Math.round(staff.baseMinutesPerFortnight * staff.fte);
  const sumMinutes = (classes: LoadLetterClass[]) => classes.reduce((sum, c) => sum + c.minutesPerFortnight, 0);

  const semesterLoads = {
    semester1: computeSemesterLoad('semester1', sumMinutes(semester1Classes), sumMinutes(ruahTerm1Classes), allowances, targetMinutes),
    semester2: computeSemesterLoad('semester2', sumMinutes(semester2Classes), sumMinutes(ruahTerm3Classes), allowances, targetMinutes),
  };

  // A Full Year letter used to ADD both semesters and compare the total with one
  // fortnight's target, roughly doubling utilisation (#290). It now shows each
  // semester separately; its summary fields (letter list, {{utilization}} etc.)
  // carry the busier semester so overload stays visible.
  const headline: SemesterLoad =
    options.semester === 'semester1' ? semesterLoads.semester1
      : options.semester === 'semester2' ? semesterLoads.semester2
      : semesterLoads.semester2.utilization > semesterLoads.semester1.utilization
        ? semesterLoads.semester2
        : semesterLoads.semester1;

  const {
    regularClassMinutes: regularTeachingMinutes,
    ruahMinutes: ruahCountedMinutes,
    teachingMinutes,
    allowanceMinutes,
    allocatedMinutes,
    timetabledSubs,
    utilization,
  } = headline;

  // Fetch settings for college info
  const settings = await getLoadLetterSettings();

  // Extract special allowances (Oratory Leader, POL/R Position)
  const oratoryAllowance = allowances.find(a =>
    a.type.toLowerCase().includes('oratory') || a.description?.toUpperCase().includes('MP')
  );
  const polAllowance = allowances.find(a =>
    a.type.toLowerCase().includes('pol') ||
    a.type.toLowerCase().includes('position of leadership') ||
    a.type.toLowerCase().includes('position of responsibility')
  );

  // Calculate prior year
  const priorYear = (parseInt(options.academicYear) - 1).toString();

  // Build template variables - use staffCode (new internal codes)
  const variables: TemplateVariables = {
    staffName: staff.name,
    staffCode: staff.staffCode, // Use NEW staff code only (AC, AJE, etc.)
    department: staff.department,
    curriculumArea: staff.curriculumArea,
    academicYear: options.academicYear,
    semester: getSemesterDisplayName(options.semester),
    semesterKey: options.semester,
    issueDate: issueDate.toISOString().split('T')[0],
    dueDate: dueDate.toISOString().split('T')[0],
    fte: staff.fte,
    targetMinutes,
    allocatedMinutes,
    teachingMinutes,
    allowanceMinutes,
    timetabledSubs,
    utilization,
    regularClassMinutes: regularTeachingMinutes,
    ruahClassMinutes: ruahCountedMinutes,
    semesterLoads: options.semester === 'both' ? semesterLoads : undefined,
    semester1Classes,
    semester2Classes,
    allowances,

    // RUAH term-based classes
    ruahTerm1Classes,
    ruahTerm2Classes,
    ruahTerm3Classes,
    ruahTerm4Classes,

    // College info from settings
    collegeName: settings?.collegeName,
    collegeAddress: settings?.collegeAddress,
    collegePhone: settings?.collegePhone,
    collegeEmail: settings?.collegeEmail,
    collegeWebsite: settings?.collegeWebsite,
    collegeTagline: settings?.collegeTagline,
    collegeLogoUrl: settings?.collegeLogoUrl,

    // Principal info
    principalName: settings?.principalName,
    principalSignature: settings?.principalSignatureUrl,

    // Letter-specific
    priorYear,
    meaGuidelinesUrl: 'https://www.scrvic.edu.au/mea-guidelines', // TODO: Add to settings
    oratoryLeader: oratoryAllowance?.description || oratoryAllowance?.type,
    oratoryMinutes: oratoryAllowance?.minutesPerFortnight,
    polPosition: polAllowance?.type,
    polMinutes: polAllowance?.minutesPerFortnight,
    otherAllowance: allowanceMinutes,
  };

  // Render HTML from template
  const generatedHtml = renderTemplate(template, variables);

  // Build load letter object
  const loadLetter: Omit<LoadLetter, 'id' | 'createdAt' | 'updatedAt'> = {
    staffId: staff.id,
    staffName: staff.name,
    staffCode: staff.staffCode, // Use NEW staff code only (AC, AJE, etc.)
    department: staff.department,
    curriculumArea: staff.curriculumArea,
    academicYear: options.academicYear,
    semester: options.semester,
    issueDate: issueDate.toISOString().split('T')[0],
    dueDate: dueDate.toISOString().split('T')[0],
    status: 'ready', // Start as 'ready' per user preference
    generatedDate: now.toISOString(),
    fte: staff.fte,
    baseMinutesPerFortnight: staff.baseMinutesPerFortnight,
    targetMinutes,
    allocatedMinutes,
    teachingMinutes,
    allowanceMinutes,
    timetabledSubs,
    utilization,
    semester1Classes,
    semester2Classes,
    ruahTerm1Classes,
    ruahTerm2Classes,
    ruahTerm3Classes,
    ruahTerm4Classes,
    allowances,
    templateId: template.id,
    generatedHtml,
    queries: [],
    createdBy: options.userId,
  };

  return loadLetter;
};

/**
 * Extract classes for a specific semester
 */
const extractClassesForSemester = (
  staff: StaffData,
  grid: GridData,
  lines: LineData[],
  semester: 'semester1' | 'semester2'
): LoadLetterClass[] => {
  const classes: LoadLetterClass[] = [];

  // Validate inputs
  if (!lines || !Array.isArray(lines)) {
    throw new Error(`extractClassesForSemester: lines is not an array (type: ${typeof lines})`);
  }

  // Get lines for this semester. A full-year line runs in both semesters, so it
  // counts in each (as gridLoadLetterService does). Callers map full-year lines
  // to semester1, so go by studyPeriod rather than `semester` (#290).
  const semesterLines = lines.filter(l => l && (l.semester === semester || l.studyPeriod === 'fullyear'));
  const semesterLineIds = semesterLines.map(l => l.id);

  // Check each class in the grid
  if (grid && grid.classes) {
    grid.classes
      .filter(classItem => !classItem.isCompositeSubClass) // Filter out composite sub-classes to avoid double-counting
      .forEach((classItem: any) => {
        // Skip classes from RUAH grid (they're handled by extractRuahClasses)
        const isFromRuahGrid = classItem.sourceGridName?.toUpperCase().includes('RUAH');
        if (isFromRuahGrid) {
          return; // Skip RUAH grid classes
        }

      // Check line assignments for this class
      if (classItem.lineAssignments) {
        Object.entries(classItem.lineAssignments).forEach(([lineId, assignedTeacher]) => {
          if (!semesterLineIds.includes(lineId)) return;

          let teacherMinutes = resolveClassMinutes(classItem, grid);
          let teacherMatches = false;

          if (!assignedTeacher) return;

          // Parse multi-teacher JSON
          let parsedTeacher = assignedTeacher;
          if (typeof assignedTeacher === 'string' && assignedTeacher.startsWith('[')) {
            try {
              parsedTeacher = JSON.parse(assignedTeacher);
            } catch (e) {
              return;
            }
          }

          // Handle multi-teacher or single teacher
          if (Array.isArray(parsedTeacher)) {
            // EXACT same logic as LoadSummaries.tsx line 155-159
            const teacherAssignment = parsedTeacher.find((t: any) => {
              const matchesCode = t.teacherCode === staff.staffCode;
              const matchesId = t.teacherCode === staff.id;
              return matchesCode || matchesId;
            });
            if (teacherAssignment) {
              teacherMatches = true;
              teacherMinutes = teacherAssignment.minutes || teacherMinutes;
            }
          } else if (typeof parsedTeacher === 'string') {
            // EXACT same logic as LoadSummaries.tsx line 166
            teacherMatches = parsedTeacher === staff.staffCode || parsedTeacher === staff.id;
          }

          if (teacherMatches) {
            classes.push({
              id: `${classItem.id}-${lineId}`,
              classCode: classItem.classCode || '',
              className: classItem.className || classItem.subjectName || '',
              subjectCode: classItem.subjectCode || '',
              subjectName: classItem.subjectName || '',
              yearLevel: classItem.yearLevel || 0,
              semester,
              periodsPerWeek: Math.round(teacherMinutes / 110),
              minutesPerFortnight: teacherMinutes,
              numberOfStudents: classItem.numberOfStudents,
            });
          }
        });
      }
    });
  }

  // Sort by year level and subject
  classes.sort((a, b) => {
    if (a.yearLevel !== b.yearLevel) return a.yearLevel - b.yearLevel;
    return a.subjectName.localeCompare(b.subjectName);
  });

  return classes;
};

/**
 * Extract RUAH classes for a specific term
 */
export const extractRuahClasses = (
  staff: StaffData,
  grid: GridData,
  lines: LineData[],
  term: 'term1' | 'term2' | 'term3' | 'term4'
): LoadLetterClass[] => {
  const classes: LoadLetterClass[] = [];

  // Validate inputs
  if (!lines || !Array.isArray(lines)) {
    throw new Error(`extractRuahClasses: lines is not an array (type: ${typeof lines})`);
  }

  // Get lines for this term (based on line name or studyPeriod)
  // RUAH lines are typically named: Line A = Term 1, Line B = Term 2, Line C = Term 3, Line D = Term 4
  const termLines = lines.filter(l => {
    if (!l) return false;
    // Use name if available, otherwise fallback to id
    const lineName = (l.name?.toUpperCase() || '');
    const lineId = (l.id?.toUpperCase() || '');
    const searchText = lineName || lineId;

    // Check studyPeriod first (if explicitly set to term1, term2, etc.)
    if (l.studyPeriod === term || l.semester === term) {
      return true;
    }

    // Map line names/IDs to terms for RUAH grid
    // Line A = Term 1, Line B = Term 2, Line C = Term 3, Line D = Term 4
    if (term === 'term1' && searchText.includes('A')) return true;
    if (term === 'term2' && searchText.includes('B')) return true;
    if (term === 'term3' && searchText.includes('C')) return true;
    if (term === 'term4' && searchText.includes('D')) return true;

    return false;
  });
  const termLineIds = termLines.map(l => l.id);

  // Check each class in the grid
  if (grid && grid.classes) {
    grid.classes
      .filter(classItem => !classItem.isCompositeSubClass) // Filter out composite sub-classes to avoid double-counting
      .forEach((classItem: any) => {
        // Only extract classes from RUAH grid
        const isFromRuahGrid = classItem.sourceGridName?.toUpperCase().includes('RUAH');
        if (!isFromRuahGrid) {
          return; // Skip non-RUAH grid classes
        }

      // Check line assignments for this class
      if (classItem.lineAssignments) {
        Object.entries(classItem.lineAssignments).forEach(([lineId, assignedTeacher]: [string, any]) => {
          // Skip if not the right term
          if (!termLineIds.includes(lineId)) {
            return;
          }

          let teacherMinutes = resolveClassMinutes(classItem, grid, 70);
          let teacherMatches = false;

          if (!assignedTeacher) return;

          // Parse multi-teacher JSON
          let parsedTeacher = assignedTeacher;
          if (typeof assignedTeacher === 'string' && assignedTeacher.startsWith('[')) {
            try {
              parsedTeacher = JSON.parse(assignedTeacher);
            } catch (e) {
              return;
            }
          }

          // Handle multi-teacher or single teacher
          if (Array.isArray(parsedTeacher)) {
            // EXACT same logic as LoadSummaries.tsx line 155-159
            const teacherAssignment = parsedTeacher.find((t: any) => {
              const matchesCode = t.teacherCode === staff.staffCode;
              const matchesId = t.teacherCode === staff.id;
              return matchesCode || matchesId;
            });
            if (teacherAssignment) {
              teacherMatches = true;
              teacherMinutes = teacherAssignment.minutes || teacherMinutes;
            }
          } else if (typeof parsedTeacher === 'string') {
            // EXACT same logic as LoadSummaries.tsx line 166
            teacherMatches = parsedTeacher === staff.staffCode || parsedTeacher === staff.id;
          }

          if (teacherMatches) {
            const minutesPerFortnight = teacherMinutes;

            classes.push({
              id: `${classItem.id}-${lineId}`,
              classCode: classItem.classCode || '',
              className: classItem.className || classItem.subjectName || '',
              subjectCode: classItem.subjectCode || '',
              subjectName: classItem.subjectName || '',
              yearLevel: classItem.yearLevel || 0,
              semester: term === 'term1' || term === 'term2' ? 'semester1' : 'semester2', // Map to semester for compatibility
              periodsPerWeek: Math.round(minutesPerFortnight / 110),
              minutesPerFortnight: minutesPerFortnight,
              numberOfStudents: classItem.numberOfStudents,
              term: term,
              isRuah: true
            });
          }
        });
      }
    });
  }

  // Sort by subject name
  classes.sort((a, b) => a.subjectName.localeCompare(b.subjectName));

  return classes;
};

/**
 * Clean allowance text by removing import metadata suffix
 * Safety filter for any remaining "- Imported from POL data" text
 * Handles variations in spacing, punctuation, and case
 * Works for both name and description fields
 */
const cleanAllowanceText = (text: string): string => {
  if (!text) return '';
  // More flexible regex to catch variations:
  // \s* = optional whitespace
  // [-–—] = dash, en-dash, or em-dash
  // \s* = optional whitespace
  // case insensitive match for "Imported from POL data"
  return text
    .replace(/\s*[-–—]\s*Imported\s+from\s+POL\s+data\s*$/i, '')
    .trim();
};

/**
 * Extract allowances for the specified semester and academic year.
 *
 * Allowances for every year live side by side on the staff record (the v2.11.0
 * backfill stamped each as 2026 and added an `allowance_2027_` copy), so without
 * the year filter a 2027 letter lists both. The id de-dupe below can't catch
 * that because the copies have distinct ids.
 */
export const extractAllowances = (
  staff: StaffData,
  semester: Semester,
  year: number
): LoadLetterAllowance[] => {
  const allowances: LoadLetterAllowance[] = [];
  const forYear = (list: any[]) => list.filter(a => allowanceAppliesToYear(a, year));

  // Allowances are pro-rated by FTE so the load letter matches the grid/load-summary
  // views, which already scale allowance minutes by FTE (see getEffectiveAllowanceMinutes
  // and AllocationContext). Fixed-time allowances are entered pre-inflated so that
  // entered × FTE lands on the intended minutes.
  const fte = staff.fte || 1.0;
  const scale = (minutes: number) => getEffectiveAllowanceMinutes(minutes, fte);

  // Get semester-specific allowances
  if (semester === 'semester1' || semester === 'both') {
    if (staff.semester1?.allowances) {
      forYear(staff.semester1.allowances).forEach((allowance: any) => {
        allowances.push({
          id: allowance.id,
          type: cleanAllowanceText(allowance.name),
          description: cleanAllowanceText(allowance.description || ''),
          minutesPerFortnight: scale(allowance.minutes),
          semester: 'semester1',
        });
      });
    }
  }

  if (semester === 'semester2' || semester === 'both') {
    if (staff.semester2?.allowances) {
      forYear(staff.semester2.allowances).forEach((allowance: any) => {
        allowances.push({
          id: allowance.id,
          type: cleanAllowanceText(allowance.name),
          description: cleanAllowanceText(allowance.description || ''),
          minutesPerFortnight: scale(allowance.minutes),
          semester: 'semester2',
        });
      });
    }
  }

  // Get 'both' semester allowances (apply to all)
  // Handle both possible structures: {allowances: [...]} or just [...]
  const bothAllowances = Array.isArray(staff.both)
    ? staff.both
    : staff.both?.allowances;

  if (bothAllowances && Array.isArray(bothAllowances)) {
    forYear(bothAllowances).forEach((allowance: any) => {
      allowances.push({
        id: allowance.id,
        type: cleanAllowanceText(allowance.name),
        description: cleanAllowanceText(allowance.description || ''),
        minutesPerFortnight: scale(allowance.minutes),
        semester: 'both',
      });
    });
  }

  // Remove duplicates and sort
  const uniqueAllowances = allowances.filter(
    (allowance, index, self) =>
      index === self.findIndex(a => a.id === allowance.id)
  );

  uniqueAllowances.sort((a, b) => a.type.localeCompare(b.type));

  return uniqueAllowances;
};

/**
 * One semester's load, per fortnight, so it compares directly with the target.
 * Allowances count when they apply to this semester or to both.
 */
export const computeSemesterLoad = (
  semester: 'semester1' | 'semester2',
  regularClassMinutes: number,
  ruahMinutes: number,
  allowances: LoadLetterAllowance[],
  targetMinutes: number
): SemesterLoad => {
  const teachingMinutes = regularClassMinutes + ruahMinutes;
  const allowanceMinutes = allowances
    .filter(a => a.semester === semester || a.semester === 'both')
    .reduce((sum, a) => sum + a.minutesPerFortnight, 0);
  const allocatedMinutes = teachingMinutes + allowanceMinutes;
  return {
    semester,
    regularClassMinutes,
    ruahMinutes,
    teachingMinutes,
    allowanceMinutes,
    allocatedMinutes,
    timetabledSubs: Math.max(0, targetMinutes - allocatedMinutes),
    utilization: targetMinutes > 0 ? Math.round((allocatedMinutes / targetMinutes) * 100) : 0,
  };
};

/**
 * Get display name for semester
 */
const getSemesterDisplayName = (semester: Semester): string => {
  switch (semester) {
    case 'semester1':
      return 'Semester 1';
    case 'semester2':
      return 'Semester 2';
    case 'both':
      return 'Full Year';
    default:
      return 'Semester 1';
  }
};

/**
 * Batch generate load letters for multiple staff
 */
export const batchGenerateLoadLetters = async (
  staffList: StaffData[],
  grid: GridData,
  lines: LineData[],
  options: GenerateOptions,
  onProgress?: (current: number, total: number) => void
): Promise<{
  letters: Array<Omit<LoadLetter, 'id' | 'createdAt' | 'updatedAt'>>;
  errors: Array<{ staffId: string; staffName: string; staffCode: string; error: string }>;
}> => {
  const letters: Array<Omit<LoadLetter, 'id' | 'createdAt' | 'updatedAt'>> = [];
  const errors: Array<{ staffId: string; staffName: string; staffCode: string; error: string }> = [];

  for (let i = 0; i < staffList.length; i++) {
    const staff = staffList[i];

    try {
      console.log(`[Load Letters] Generating ${i + 1}/${staffList.length}: ${staff.name} (${staff.staffCode || staff.id})`);
      const letter = await generateLoadLetter(staff, grid, lines, options);
      letters.push(letter);

      if (onProgress) {
        onProgress(i + 1, staffList.length);
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      console.error(`❌ [Load Letters] FAILED for ${staff.name} (${staff.staffCode || staff.id}):`, errorMsg);
      errors.push({
        staffId: staff.id,
        staffName: staff.name,
        staffCode: staff.staffCode || staff.id,
        error: errorMsg
      });
      // Continue with other staff members
    }
  }

  console.log(`[Load Letters] Generation complete: ${letters.length} successful, ${errors.length} failed`);
  return { letters, errors };
};
